/**
 * /logout — dedicated route that runs the global Cognito sign-out flow
 * then bounces to the sign-in surface. Legacy 3mote-fe parity: bookmarks
 * and email links pointing at this URL must work without 404'ing.
 *
 * Renders a tiny "Signing out…" placeholder while the async flow runs.
 * The flow itself:
 *   1. await signOut() — fires Cognito globalSignOut + clears cookies.
 *   2. On deployed tenant hosts, hard-REPLACE to the CENTRAL auth origin
 *      (`login[-staging].3mote.com/login?return=<tenant>`) — that page's
 *      SSO buttons are real one-click embeds, so signed-out users land
 *      where sign-in actually starts (David's UX call, 2026-09-01).
 *      Localhost (and any host without a central twin) keeps the local
 *      /login fallback — `.3mote.com` cookies can't reach localhost.
 *      `replace`, not `assign`, so back-button never re-runs /logout.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { signOutToLogin } from '@/features/auth/sign-out-redirect'

export const Route = createFileRoute('/logout')({
  component: LogoutPage,
})

function LogoutPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false
    void signOutToLogin(() => {
      if (!cancelled) {
        navigate({ to: '/login', replace: true })
      }
    })
    return () => {
      cancelled = true
    }
  }, [navigate])

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-muted">
      <p className="text-sm text-fg-muted">{t('logout.signingOut')}</p>
    </div>
  )
}
